import axios, { Canceler } from 'axios';
import moment, { Moment } from 'moment';
import { AVAILABILITY_OPTIONS, LOCALES, PAYMENT_TYPES } from '~/constants/main';
import { findSubstring } from './parser';
import { debounce, memoize } from './perfomance';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE, DELIVERY_TYPES, ORDER_DELIVERY_TYPES } from 'src/constants/deliveryTypes';
import { RESTRICTION_ACCESS } from 'src/constants/integrations';
import { CALL_CENTER_GROUP, LIVE_ORDERS_TYPES_GROUP, ORDERS_GROUPS, ORDER_STATUS, ORDER_TYPE } from 'src/constants/order';
import { USER_ROLES } from 'src/constants/user';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import cookies from 'src/services/cookie';
import wms from 'src/services/wms';
import { TDeviceType } from 'src/types/app';
import { IBranch, ICurrencySettings, UNAVAILABLE_BRANCHES_FLAG } from 'src/types/branches';
import { TBooleanInNumber, TLocale, TTranslate } from 'src/types/main';
import { PRODUCT_ORDER_TYPE } from 'src/types/product';
import { floatNumbersValidation } from 'src/utils/validation';
import { PROMOCODE_DISCOUNT_TYPE, PROMOCODE_PROVIDE_AS } from 'src/view/screens/MarketingScreen/screens/PromocodesScreen/types/promocodes';
import { defaultDateFormat } from '~/constants/dateTime';
import { TOrder } from '~/types/orders';
import { CashbackErrorCode } from '~/constants/errors';

// eslint-disable-next-line promise/avoid-new
export const delay = async (ms: number = 1000) => new Promise((resolve) => setTimeout(resolve, ms));

export const isObject = (data: any) => {
  if (typeof data !== 'object' || Array.isArray(data) || !(data instanceof Object)) return false;
  return typeof data === 'object';
};

export const isJson = (data: any) => {
  try {
    JSON.parse(data);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * return translate for selected language or will return incoming parameter
 *
 * @param {(TTranslate | string)} [translate]
 * @return {*}
 */
export const getTranslate = (translate?: TTranslate | string, locale?: LOCALES.AR | LOCALES.EN): string => {
  if (!translate) return '';

  if (locale) {
    return typeof translate === 'string' ? localeStore.locales[locale]?.[translate] || translate : translate?.[locale] || '';
  }

  return typeof translate === 'string' ? translate : translate[localeStore.language] || '';
};

export const getDeviceType = (): TDeviceType => {
  let device: TDeviceType = 'desktop';
  switch (true) {
    case window.innerWidth > 1280:
      device = 'desktop';
      break;
    case window.innerWidth <= 1280 && window.innerWidth > 991:
      device = 'tablet';
      break;
    case window.innerWidth <= 991:
      device = 'mobile';
      break;

    default:
      device = 'desktop';
      break;
  }
  return device;
};

export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const copyToBuffer = (data: string | number | undefined) => {
  if (typeof data !== 'string') return;
  if (window.IS_MOBILE) {
    wms.copyToClipboard(data);
    return;
  }
  navigator.clipboard.writeText(data);
};

export const downloadFile = (file: any, fileName: string) => {
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(file);
  link.download = fileName;
  link.href = url;
  link.click();
  link.remove();
};

export const makeBlob = (dataURL: string) => {
  const BASE64_MARKER = ';base64,';

  let parts: string[] = [''];
  let contentType: string = '';
  let raw: string = '';

  if (dataURL.indexOf(BASE64_MARKER) === -1) {
    parts = dataURL.split(',');
    contentType = parts[0].split(':')[1];
    raw = decodeURIComponent(parts[1]);
    return new Blob([raw], { type: contentType });
  } else {
    parts = dataURL.split(BASE64_MARKER);
    contentType = parts[0].split(':')[1];
    raw = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(raw.length);

    for (let i = 0; i < raw.length; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
  }
};

export const booleanToNumber = (boolean: boolean | TBooleanInNumber | number): TBooleanInNumber => (boolean ? 1 : 0);

export const booleanNumberReverse = (number: number | TBooleanInNumber | boolean | undefined): TBooleanInNumber => +!number as TBooleanInNumber;

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function getDefaultLanguage(default_language?: TLocale): TLocale {
  let language: TLocale = LOCALES.EN;
  const userLanguage = new URLSearchParams(window.location.search).get('language') || navigator.language || default_language || LOCALES.EN;
  switch (true) {
    case userLanguage.includes(LOCALES.AR):
      language = LOCALES.AR;
      break;

    default:
      language = LOCALES.EN;
      break;
  }
  return language;
}

export const getOrderType = (order: TOrder): ORDER_TYPE => {
  let type: ORDER_TYPE = ORDER_TYPE.ACTIVE;
  if (!order) return type;

  switch (true) {
    case [ORDER_STATUS.DRAFT, ORDER_STATUS.WAITING_FOR_PAYMENT].includes(order.order_status_code):
      type = ORDER_TYPE.DRAFT;
      break;
    // @ts-ignore
    case [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELED].includes(order.order_status_code):
      type = ORDER_TYPE.HISTORY;
      break;
    case order.ahead === 0:
      type = ORDER_TYPE.AHEAD;
      break;
    default:
      type = ORDER_TYPE.ACTIVE;
      break;
  }
  return type;
};

export const getPaymentMethodIcon = (type: PAYMENT_TYPES): Icons => {
  switch (type) {
    case PAYMENT_TYPES.TERMINAL:
      return Icons.Terminal;
    case PAYMENT_TYPES.ONLINE_PAYMENT:
      return Icons.Card;
    default:
      return Icons.Cash;
  }
};

export const getOrderStatusGroup = (status: ORDER_STATUS): ORDERS_GROUPS => {
  let group: [ORDERS_GROUPS, ORDER_STATUS[]] | undefined;

  if (true) {
    group = (Object.entries(CALL_CENTER_GROUP) as [ORDERS_GROUPS, ORDER_STATUS[]][]).find(
      ([name, statuses]) => name !== ORDERS_GROUPS.ALL && statuses.includes(status)
    );
  } else {
    group = (Object.entries(LIVE_ORDERS_TYPES_GROUP) as [ORDERS_GROUPS, ORDER_STATUS[]][]).find(
      ([name, statuses]) => name !== ORDERS_GROUPS.ALL && name !== ORDERS_GROUPS.AHEAD && statuses.includes(status)
    );
  }
  return group ? group[0] : ORDERS_GROUPS.NEW;
};

export const getDiscountType = (type: PROMOCODE_DISCOUNT_TYPE, provideAs: PROMOCODE_PROVIDE_AS) => {
  if (type === PROMOCODE_DISCOUNT_TYPE.AMOUNT) {
    if (provideAs === PROMOCODE_PROVIDE_AS.CASHBACK) {
      return localeStore.t('promocode_fixed_cashback_type');
    }
    return localeStore.t('amount_based');
  } else {
    if (provideAs === PROMOCODE_PROVIDE_AS.CASHBACK) {
      return localeStore.t('promocode_percent_cashback_type');
    }
    return localeStore.t('percentage_based');
  }
};

export const getGoogleMapAddress = (lat: number, lng: number) => {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

export const switchToAdminVersion = (isNew: boolean) => {
  cookies.setCookie('is_new_admin', isNew ? '1' : '0');
  window.location.href = '/admin/dashboard';
};

export const hasPermissions = (permissions: string[]): boolean => {
  if (!permissions.length || (!userStore.user.permissions.length && userStore.user.role === USER_ROLES.RESTAURANT_MANAGER)) return true;
  return permissions.some((permission) => userStore.user.permissions.includes(permission));
};

export const hasBranch = memoize((branch: IBranch['id']): boolean => {
  if (!userStore.user.branches.length) return false;
  return userStore.user.branches.some((user_branch) => user_branch.id === branch);
});

export const hasRole = (roles: string[]): boolean => {
  if (!roles.length) return true;
  return roles.includes(userStore.user.role);
};

export const generateRandomString = (length: number = 8) => {
  const string: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  let random: string = '';
  while (random.length < length) {
    random += string[Math.floor(Math.random() * string.length)];
  }
  return random;
};

// with surrogate couples
export const stringLength = (string: string) => {
  let length = 0;
  // eslint-disable-next-line
  for (const c of string) {
    length += 1;
  }
  return length;
};

// cuts off unnecessary 0 and dot in front, replaces "," with "." (if this is not done, then when converted to a number it will give NaN)
export const stringTransform = (value: string, disableRemoveUnnecessaryZeros: boolean = false): string => {
  value = value.split(',').join('.').trim().replace(/^\./, '');
  const needRemoveUnnecessaryZeros: boolean = !disableRemoveUnnecessaryZeros && value.length > 1 && value[1] !== '.';

  return needRemoveUnnecessaryZeros ? value.replace(/^0+/, '') : value;
};

export const toPickerOptions = (items: { id: number; name: string | TTranslate; data?: any }[]) => {
  const options: any = items.map((item) => ({ label: getTranslate(item.name), value: item.id, data: item?.data }));
  return options;
};

export const getDeliveryType = (delivery_type?: BRANCH_DELIVERY_TYPE): DELIVERY_TYPE => {
  if (
    (delivery_type === BRANCH_DELIVERY_TYPE.DELIVERY || delivery_type === BRANCH_DELIVERY_TYPE.BOTH) &&
    (appStore.settings?.delivery_type === DELIVERY_TYPES.BOTH || appStore.settings?.delivery_type === DELIVERY_TYPES.DELIVERY)
  ) {
    return DELIVERY_TYPE.DELIVERY;
  } else if (
    (delivery_type === BRANCH_DELIVERY_TYPE.PICKUP || delivery_type === BRANCH_DELIVERY_TYPE.BOTH) &&
    (appStore.settings?.delivery_type === DELIVERY_TYPES.BOTH || appStore.settings?.delivery_type === DELIVERY_TYPES.PICKUP)
  ) {
    return DELIVERY_TYPE.PICKUP;
  } else if (!delivery_type) {
    if (appStore.settings?.delivery_type === DELIVERY_TYPES.BOTH || appStore.settings?.delivery_type === DELIVERY_TYPES.PICKUP) {
      return DELIVERY_TYPE.PICKUP;
    }
    if (appStore.settings?.delivery_type === DELIVERY_TYPES.DELIVERY) {
      return DELIVERY_TYPE.DELIVERY;
    }
  }
  return DELIVERY_TYPE.PICKUP;
};

/**
 * Indicates whether a branch is available for delivery with the selected delivery type.
 * It is also possible to filter by branch name.
 *
 * @param {IBranch} branch
 * @param {DELIVERY_TYPE} deliveryType - selected delivery type
 * @param {string} [searchValue] - string to filter by branch name
 * @returns
 */
export const isBranchAvailableToDelivery = (branch: IBranch, deliveryType: DELIVERY_TYPE, searchValue: string = ''): boolean => {
  switch (true) {
    case !hasBranch(branch.id):
      return false;
    case branch.is_busy || !branch.status:
      return false;
    case deliveryType === DELIVERY_TYPE.DELIVERY && ![BRANCH_DELIVERY_TYPE.BOTH, BRANCH_DELIVERY_TYPE.DELIVERY].includes(branch.delivery_type):
      return false;
    case deliveryType === DELIVERY_TYPE.PICKUP && ![BRANCH_DELIVERY_TYPE.BOTH, BRANCH_DELIVERY_TYPE.PICKUP].includes(branch.delivery_type):
      return false;
    case deliveryType === DELIVERY_TYPE.DINE_IN && !branch.is_emenu:
      return false;
    case !findSubstring(getTranslate(branch.name), searchValue):
      return false;
    default:
      return true;
  }
};

export const getTimeUntilEndDay = (type?: 'seconds' | 'milliseconds') => {
  const timeUntilEndDay = -moment().diff(moment('24:00:00', 'hh:mm:ss'), type ? type : 'milliseconds');

  return timeUntilEndDay;
};

export const isOrderPreorder = (delivery_type_code: TOrder['delivery_type_code']) =>
  [ORDER_DELIVERY_TYPES.DELIVERY_TIME, ORDER_DELIVERY_TYPES.PICKUP_TIME].includes(delivery_type_code);

export const getUnavailable = (item: { is_delete?: TBooleanInNumber; status?: TBooleanInNumber }, flag?: UNAVAILABLE_BRANCHES_FLAG) => {
  if (flag === UNAVAILABLE_BRANCHES_FLAG.DELETE) return Boolean(item.is_delete);
  if (flag === UNAVAILABLE_BRANCHES_FLAG.STATUS) return Boolean(!item.status);
  return Boolean(item.is_delete || !item.status);
};

export const arrayCompare = (_arrayOne: any[], _arrayTwo: any[], strict?: boolean): boolean => {
  if (_arrayOne.length !== _arrayTwo.length) return false;
  if (strict) return Boolean(JSON.stringify(_arrayOne) === JSON.stringify(_arrayTwo));
  const arrayOne: string[] = _arrayOne.map((item) => JSON.stringify(item));
  const arrayTwo: string[] = _arrayTwo.map((item) => JSON.stringify(item));
  return !Boolean(arrayOne.filter((i) => !arrayTwo.includes(i)).concat(arrayTwo.filter((i) => !arrayOne.includes(i))).length);
};

export const arraySort = (array: any[], key: any) => {
  return array.slice().sort((a, b) => {
    return a[key]?.toString().toLowerCase().trim() < b[key]?.toString().toLowerCase().trim() ? -1 : 1;
  });
};

/**
 *
 * date/time format with correct translate AM/PM
 * if there are no parameters, then only the date
 * militaryFormat - 24 hours format
 *
 */
export const formatDate = (
  date: Moment | Date | string | number | null | undefined,
  params?: {
    onlyTime?: boolean;
    includeSecond?: boolean;
    militaryFormat?: boolean;
    timeFormat?: string;
  }
) => {
  if (!date) return '';
  if (!params) return moment(date).format(defaultDateFormat);

  const _date: string = params.onlyTime ? '' : `${defaultDateFormat}, `;
  const _time: string = params.timeFormat || `${params.militaryFormat ? 'H' : 'h'}:mm${params.includeSecond ? ':ss' : ''}`;
  const _timeFormat: string = params.militaryFormat ? '' : ` [${localeStore.t(moment(date).format('A'))}]`;

  return moment(date).format(_date + _time + _timeFormat);
};

export const randomFloatNumber = (): number => {
  return Date.now() * Math.random();
};

export const scrollToElement = debounce((data: { id?: string; selector?: string; scrollParams?: ScrollIntoViewOptions }) => {
  let element = null;

  if (data.id) element = document.getElementById(data.id);
  if (data.selector) element = document.querySelector(data.selector);

  if (element) element.scrollIntoView({ behavior: 'smooth', ...data.scrollParams });
}, 100);

export const scrollToErrorElement = () => {
  scrollToElement({ selector: '.input-error', scrollParams: { block: 'center' } });
};

export const getAvailabilityParams = (availability: AVAILABILITY_OPTIONS | null | undefined) => {
  const params: any = {};
  if (availability === AVAILABILITY_OPTIONS.AVAILABLE) {
    params.status = 1;
  } else if (availability === AVAILABILITY_OPTIONS.NOT_AVAILABLE) {
    params.status = 0;
  } else if (availability === AVAILABILITY_OPTIONS.DELETED) {
    params.is_delete = 1;
  }
  return params;
};

export const emptyArray = (length: number = 3): undefined[] => {
  if (!length) return [];
  return [...new Array(length)];
};

export const getUniqueArrayItems = <T extends { id: string | number }>(arr: T[]): T[] => {
  return [...new Map(arr.map((m) => [m.id, m])).values()];
};

export const sumArray = (arr: number[]): number => arr.reduce((sum, curr) => sum + curr, 0);

export const getLastArrayItem = <T>(array: T[] | undefined): T | undefined => {
  if (!array || !array.length) return undefined;
  return array.slice().shift();
};

export const isOrderTypeSupported = (type: number, supportedType: number | undefined | null): boolean => {
  const _supportedType: number = supportedType || 0;
  // bitwise sum of types
  const bitSummType: number = type & _supportedType;
  // if there are no intersections, 0 will be returned
  return Boolean(bitSummType & _supportedType);
};

export const getCancelToken = (callback: (cancel: Canceler) => void) => {
  return new axios.CancelToken((c) => callback(c));
};

export const getOrderTypeIconsNames = (orderType: number | undefined): Icons[] => {
  const iconNames: Icons[] = [];

  if (!orderType) return iconNames;

  if (isOrderTypeSupported(orderType, PRODUCT_ORDER_TYPE.PICKUP_CURBSIDE)) {
    iconNames.push(...[Icons.ShoppingBag, Icons.Curbside]);
  }
  if (isOrderTypeSupported(orderType, PRODUCT_ORDER_TYPE.DELIVERY)) {
    iconNames.push(Icons.Delivery);
  }
  if (isOrderTypeSupported(orderType, PRODUCT_ORDER_TYPE.DINEIN)) {
    iconNames.push(Icons.DineIn);
  }

  return iconNames;
};

export const countsUpdate = (currentCount: number | undefined, restore?: boolean): number => {
  if (restore) return currentCount ? currentCount + 1 : 1;
  return currentCount ? currentCount - 1 : 0;
};

export const getLocaleKeyFromError = (error: any) => {
  const msg = error.response?.data.message;
  const key = 'messages.';
  const find = msg?.indexOf(key);
  if (msg && find !== -1) return msg.substring(+key.length);
  return msg;
};

export const isDisabledByPos = (access: RESTRICTION_ACCESS | undefined, ignoredAccess?: boolean) => {
  return !ignoredAccess && access === RESTRICTION_ACCESS.DISABLED;
};

/**
 * Get Wallet Currency based on language
 *
 * @return {string}
 */
export const getWalletCurrency = () => {
  let currency = localeStore.language === LOCALES.AR ? appStore.settings?.wallet_currency_ar : appStore.settings?.wallet_currency;
  if (!currency) currency = localeStore.t('wallet_currency');

  return currency;
};

/**
 * Extract filename from response headers
 *
 * @param headers
 */
export const extractFileName = (headers: any) => headers['content-disposition']!.split(';')[1].split('=')[1];

/**
 * Get currency settings from Branch or Order
 *
 * @export
 * @param  {IBranch | TOrder} [data]
 * @return {ICurrencySettings | undefined}  {(ICurrencySettings | undefined)}
 */
export const getCurrencySettings = (data?: IBranch | TOrder): ICurrencySettings | undefined => {
  if (!data?.currency) return undefined;

  const { currency } = data;
  if (currency) {
    const { currency_code, divided, currency_position } = currency;

    return {
      currency_code,
      currency_position,
      currency_divided: divided,
    };
  }
  return {};
};

/**
 * Get radix of number
 *
 * In a positional numeral system, the radix or base is the number of unique digits, including the digit zero, used to represent numbers.
 *
 * @param {number} number
 * @return {*}  {number}
 */
export const getRadix = (number: number): number => {
  if (Math.round(number / 10)) return 1 + getRadix(Math.round(number / 10));
  return 0;
};

export const swapArrayItemsByIndexes = (arr: any[], idx1: number | undefined, idx2: number | undefined): any[] => {
  if (idx1 === undefined || idx2 === undefined) return arr;

  const arrCopy = [...arr];
  [arrCopy[idx1], arrCopy[idx2]] = [arrCopy[idx2], arrCopy[idx1]];

  return arrCopy;
};

export const getWeekdays = (): string[] => {
  return moment.weekdays().map((weekday) => weekday.toLowerCase());
};

/**
 * Format date string to Date object
 *
 * @param dateString
 * @param format
 */
export const formatStringToDateObj = (dateString: string, format = 'HH:mm:ss'): Date => {
  return moment(dateString, format).toDate();
};

export const getValidatedInputNumericValue = (value: string): string | null => {
  const formattedValue = stringTransform(value);

  if (floatNumbersValidation(formattedValue, 1)) {
    return formattedValue || '0';
  }

  return null;
};

export const isFloat = (value: any) => {
  if (typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value)) {
    return true;
  }

  return false;
};

export const isCashbackErrorCode = (errCode: number) => {
  return [
    CashbackErrorCode.Unavailable,
    CashbackErrorCode.ChangedConditions,
    CashbackErrorCode.UnavailableForBranch,
    CashbackErrorCode.UnavailableForOrderType,
  ].includes(errCode);
};

export const formatBranchLabel = (name: string, address: string | null): string => {
  return address ? `${name} - ${address}` : name;
};
